import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { Paper } from '@material-ui/core';
import { useParams, useLocation } from 'react-router-dom';
import { useIsFeatureFlagEnabled } from '../contexts/feature-flag-context';
import { TabGroup } from './TabGroup';

import { POLLING_INTERVALS } from '../constants';

import {
  MyBoardsWithBucketsByPriorityAndRule,
  MyBoardsWithBucketsQuery,
  MyBoardsWithGroupedGigBucketsByPriorityAndRule,
  MyBoardsWithGroupedGigBucketsQuery
} from '../queries/myBoards';

import { useGigFilterAndSort } from '../hooks/useGigFilterAndSort';

import Loader from './Loader';
import { BoardWithBuckets } from './BoardWithBuckets';
import { Title } from './Title';
import BoardFilters from './BoardFilters/BoardFilters';

import { getNonEmptyBoards } from '../Helper';
import { BoardProvider } from '../hooks/useBoardContext';

import { useStyles } from '../theme';

const styles = (theme) => ({
  root: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    boxShadow: 'unset'
  }
});

const allLabel = (count = 0) => {
  return { id: 'all', name: 'all', displayName: 'All', boardCount: count };
};

export const MyBoardsWithBuckets = () => {
  const titleFlag = useIsFeatureFlagEnabled("vip-fe-priority-buckets-headers");
  const boardHeaderFlag = useIsFeatureFlagEnabled("vip-fe-board-headers");
  const timestampFlag = useIsFeatureFlagEnabled("vip-fe-add-last-updated");
  const priorityFiltersFlag = useIsFeatureFlagEnabled("vip-fe-priority-filters");
  const { rule, priority, boardId } = useParams();
  const { pathname } = useLocation();
  const classes = useStyles(styles);
  const tabGroupFlag = useIsFeatureFlagEnabled('vip-fe-add-tab-groups');
  const driverBucketFlag = useIsFeatureFlagEnabled('vip-fe-add-driver-buckets');

  const [boards, setBoards] = useState([]);
  const [appliedLabel, setAppliedLabel] = useState('all');
  const [labels, setLabels] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState(null);

  const isStandardBoard = pathname.indexOf('/boards/') !== -1 && !!boardId;
  const bucketTargetAccsr = driverBucketFlag ? "groupedGigs" : "gigs"

  const { appliedFilters, handleFilterChange } = useGigFilterAndSort();

  const query = isStandardBoard
    ? (driverBucketFlag ? MyBoardsWithGroupedGigBucketsQuery : MyBoardsWithBucketsQuery)
    : (driverBucketFlag ? MyBoardsWithGroupedGigBucketsByPriorityAndRule : MyBoardsWithBucketsByPriorityAndRule);

  const vars = isStandardBoard
    ? { boardId, filter: appliedFilters, priorities: priorityFilter }
    : { priority, title: rule ? rule : null, filter: appliedFilters };

  const { data, loading, error } = useQuery(query, {
    variables: {
      ...vars,
      withLabels: !!tabGroupFlag
    },
    nextFetchPolicy: 'network-only',
    pollInterval: POLLING_INTERVALS.myBoards
  });

  const findBoardsByLabel = (labelId) => {
    return labelId === 'all'
      ? data.myBoards
      : data.myBoards.filter((board) =>
          board.labels?.some((l) => l.id === labelId) && board?.bucketsByPriority?.length
        );
  };

  useEffect(() => {
    if (data) {
      if (isStandardBoard) {
        setBoards(getNonEmptyBoards([data?.board], 'buckets', bucketTargetAccsr));
      } else {
        setBoards(getNonEmptyBoards(data?.myBoards, 'bucketsByPriority', bucketTargetAccsr));
        if (data?.labels) {
          const totalBoardsCount = data.myBoards.filter((board) =>
              board?.bucketsByPriority?.length
            ).length;
          const sortedLabels = data?.labels?.toSorted((label1, label2) => label1.id - label2.id);
          const sortedLabelsWithCounts = sortedLabels?.map((label) => Object.assign({boardCount: findBoardsByLabel(label.id)?.length}, label));
          setLabels([allLabel(totalBoardsCount), ...sortedLabelsWithCounts || []]);
          setBoards(findBoardsByLabel(appliedLabel));
        }
      }
    }
    // intentional to only run when data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isStandardBoard]);

  const handleAppliedLabelChange = (labelId) => {
    setAppliedLabel(labelId);
    setBoards(findBoardsByLabel(labelId));
  };

  return (
    <Paper
      square
      className={`${classes.root}`}
      style={{ boxShadow: 'unset', paddingTop: 12 }}
    >
      {(timestampFlag ||  titleFlag) &&
        <Title
          timestamp={boards[0]?.lastCacheUpdatedAt}
          priority={priority}
          name={boardHeaderFlag ? boards[0]?.name : null}
          rule={rule}
        />
      }
      <BoardFilters
        priorityFilter={isStandardBoard ? priorityFilter : null}
        handleApplyPriorityFilter={isStandardBoard ? setPriorityFilter : null}
        appliedFilters={appliedFilters}
        handleApplyFilters={handleFilterChange}
      />
      { !isStandardBoard && labels.length > 0 && tabGroupFlag &&
          <TabGroup
            tabs={labels}
            setAppliedTab={handleAppliedLabelChange}
          />
        }
      {loading && <Loader />}
      {error && <div>{error.message}</div>}
      {!loading &&
        !error &&
        boards.length > 0 &&
        boards.map((board) => (
          <BoardProvider>
            <BoardWithBuckets
              key={board.id}
              board={board}
              priority={priority}
              rule={rule}
              isStandardBoard={isStandardBoard}
              appliedFilters={appliedFilters}
            />
          </BoardProvider>
        ))}
    </Paper>
  );
};
